import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { MyAvatar, MyButton, MyTextField, myToaster } from '@interstellar-component'
import SimpleBar from 'simplebar-react'
import { Key01, Passcode, Save01, User01 } from '@untitled-ui/icons-react'
import { encryptPassword } from '@src/services/Helper'
import { CompanyService } from '../../service'
import { useCompany } from '../../Context'
import { credentialGate } from '../agreementStatus'
import { CredentialGateNote, handleCredentialError } from '../CompanyCredentialForm'
import RejectionPanel from './RejectionPanel'

function formatActivityTimestamp(iso) {
  if (!iso) return '—'
  return moment(iso).format('h:mma D MMM YYYY')
}

export default function Result({ loading, data }) {
  const { fetchCompanyDetail, openResendPanel } = useCompany()
  const companyId = data?.id

  // Editable credential form. Username seeds from the stored value; password AND
  // API key are left blank (both are secrets). Re-enter them to (re)validate; a
  // blank api_key sends nothing and the BE upsert preserves the stored key.
  const [form, setForm] = useState({ username: '', password: '', api_key: '' })
  const [submitting, setSubmitting] = useState(false)
  // Locked until CLIK has countersigned the membership agreement.
  const gate = credentialGate(data)
  const locked = submitting || gate.blocked

  useEffect(() => {
    const g = data?.general_information
    setForm((prev) => ({
      username: g?.clik_username ?? '',
      api_key: prev.api_key,
      password: prev.password,
    }))
  }, [data?.general_information])

  const activities = useMemo(() => {
    const raw = data?.activity
    if (!Array.isArray(raw)) return []
    return [...raw].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
  }, [data?.activity])

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }))

  const handleReset = () => {
    const g = data?.general_information
    setForm({
      username: g?.clik_username ?? '',
      api_key: '',
      password: '',
    })
  }

  const handleValidate = async () => {
    if (!companyId || submitting || gate.blocked) return
    if (!form.username?.trim() || !form.password?.trim()) {
      myToaster({ message: 'Username and password are required' })
      return
    }
    setSubmitting(true)
    try {
      const res = await CompanyService.validateCredential(companyId, {
        username: form.username.trim(),
        password: encryptPassword(form.password),
        api_key: form.api_key?.trim() || undefined,
      })
      myToaster(res)
      setForm((prev) => ({ ...prev, password: '' }))
      await fetchCompanyDetail(companyId, { type: 'result' })
    } catch (err) {
      handleCredentialError(err, () => fetchCompanyDetail(companyId, { type: 'result' }))
    } finally {
      setSubmitting(false)
    }
  }

  // Open the "Reject & resend" form (slide-over) to correct the flagged items
  // and resubmit. The panel reads the company from the open detail slider.
  const handleRevise = () => openResendPanel()

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center px-4 py-12 text-sm text-gray-light/600">
        Loading…
      </div>
    )
  }

  return (
    <SimpleBar forceVisible="y" style={{ height: '100%' }}>
      <div className="flex flex-1 flex-col gap-8 pb-8 pt-4">
        <div className="flex flex-1 flex-col gap-y-6 px-4">
          {data?.verification_status === 'rejected' ? (
            <RejectionPanel
              reason={data?.verification?.note}
              reviewedAt={data?.verification?.reviewed_at}
              onRevise={handleRevise}
              submitting={submitting}
            />
          ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
              <h2 className="text-sm-semibold text-gray-900">General Information</h2>
            </div>
            <div className="flex flex-col gap-4 px-5 py-4">
              <div>
                <h3 className="text-sm-medium text-gray-800">Credentials</h3>
                <p className="mt-1 text-xs-regular leading-relaxed text-gray-600">
                  Used to authenticate and authorize API requests from system.
                </p>
              </div>
              <CredentialGateNote gate={gate} />

              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm-medium text-gray-700" htmlFor="company-result-username">
                    Username
                  </label>
                  <MyTextField
                    id="company-result-username"
                    disabled={gate.blocked}
                    name="username"
                    value={form.username}
                    onChangeForm={setField('username')}
                    placeholder="e.g. 3h3nnrf8inni871bb31884h12"
                    margin="7px 12px"
                    startAdornment={
                      <User01 className="size-[18px] text-gray-400" stroke="currentColor" />
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm-medium text-gray-700" htmlFor="company-result-password">
                    Password
                  </label>
                  <MyTextField
                    id="company-result-password"
                    disabled={gate.blocked}
                    name="password"
                    type="password"
                    value={form.password}
                    onChangeForm={setField('password')}
                    placeholder="Enter the CLIK password"
                    margin="7px 12px"
                    startAdornment={
                      <Passcode className="size-[18px] text-gray-400" stroke="currentColor" />
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm-medium text-gray-700" htmlFor="company-result-api-key">
                    API key
                  </label>
                  <MyTextField
                    id="company-result-api-key"
                    disabled={gate.blocked}
                    name="api_key"
                    value={form.api_key}
                    onChangeForm={setField('api_key')}
                    placeholder="e.g. 3h3nnrf8inni871bb31884h12"
                    margin="7px 12px"
                    startAdornment={
                      <Key01 className="size-[18px] text-gray-400" stroke="currentColor" />
                    }
                  />
                </div>
              </div>

              <hr className="border-gray-200" />
              <div className="flex justify-end gap-3 pt-1">
                <MyButton
                  color="secondary"
                  variant="outlined"
                  size="sm"
                  type="button"
                  disabled={locked}
                  onClick={handleReset}
                >
                  <p className="text-sm-semibold">Reset</p>
                </MyButton>
                <MyButton
                  color="primary"
                  variant="outlined"
                  size="sm"
                  type="button"
                  disabled={locked}
                  onClick={handleValidate}
                >
                  <Save01 className="size-4" stroke="currentColor" aria-hidden />
                  <p className="text-sm-semibold">{submitting ? 'Validating…' : 'Validate'}</p>
                </MyButton>
              </div>
            </div>
          </div>
          )}

          <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
              <h2 className="text-sm-semibold text-gray-900">Activity</h2>
            </div>
            <div className="px-4 py-5">
              {activities.length === 0 ? (
                <p className="text-sm-regular text-gray-500">No activity yet.</p>
              ) : (
                <div className="relative">
                  <div
                    className="absolute bottom-4 left-5 top-5 w-px bg-gray-200"
                    aria-hidden
                  />
                  <ul className="relative flex flex-col gap-6">
                    {activities.map((act) => (
                      <li key={act.id} className="relative flex gap-3">
                        <div className="relative z-[1] shrink-0 rounded-full bg-white p-0.5 ring-2 ring-white">
                          <MyAvatar
                            photo={act.user?.avatar_url ?? null}
                            size={40}
                          />
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-sm-medium text-gray-800">
                              {act.user?.name ?? '—'}
                            </span>
                            <time
                              className="text-sm-regular text-gray-500"
                              dateTime={act.created_at}
                            >
                              {formatActivityTimestamp(act.created_at)}
                            </time>
                          </div>
                          <p className="mt-1 text-sm-regular text-gray-600">
                            {act.action ?? '—'}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SimpleBar>
  )
}
